import { analyticsService } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { BaseExercise, Exercise, exerciseBodyParts, ExerciseComment } from "@domain/exercise/exercise";

export enum AnalyticsViewExerciseDetailOrigin {
	CANVAS = "ProgramBuilder-canvas",
	LIBRARY = "ProgramBuilder-library",
	CANVAS_EXERCISE_DETAIL = "ProgramBuilder-canvasExerciseDetail",
	EXERCISE = "Exercise"
}

export const sendAnalyticsEventViewExerciseDetail = (
	exercise: Exercise,
	origin: AnalyticsViewExerciseDetailOrigin,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.EXERCISE,
		action: "viewExerciseDetail",
		params: [
			origin,
			exercise.category,
			exerciseAnalyticsBodyPartsString(exercise),
			exercise.difficulty,
			exercise.startingPosition,
			exercise.title,
			exercise.id,
			exercise.revision,
		],
	});

export const sendAnalyticsEventAddExercise = (exercise: Exercise, category: AnalyticsCategory): void =>
	analyticsService.trackEvent({
		category,
		action: "addExercise",
		params: [
			exercise.category,
			exerciseAnalyticsBodyPartsString(exercise),
			exercise.difficulty,
			exercise.startingPosition,
			exercise.title,
			exercise.id,
			exercise.revision,
		],
	});

export const sendAnalyticsEventRemoveExercise = (exercise: Exercise, category: AnalyticsCategory): void =>
	analyticsService.trackEvent({
		category,
		action: "removeExercise",
		params: [
			exercise.category,
			exerciseAnalyticsBodyPartsString(exercise),
			exercise.difficulty,
			exercise.startingPosition,
			exercise.title,
			exercise.id,
			exercise.revision,
		],
	});

export const sendAnalyticsEventDuplicateExercise = (exercise: Exercise, category: AnalyticsCategory): void =>
	analyticsService.trackEvent({
		category,
		action: "duplicateExercise",
		params: [
			exercise.category,
			exerciseAnalyticsBodyPartsString(exercise),
			exercise.difficulty,
			exercise.startingPosition,
			exercise.title,
			exercise.id,
			exercise.revision,
		],
	});

export const sendAnalyticsEventOpenComplementaryExercises = (exercise: Exercise): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "openComplementaryExercises",
		params: [exercise.title, exercise.id],
	});

export const sendAnalyticsEventOpenAlternativeExercises = (exercise: Exercise): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "openAlternativeExercises",
		params: [exercise.title, exercise.id],
	});

export const sendAnalyticsEventAddAlternativeExercises = (
	exercise: Exercise,
	alternativeExercise: BaseExercise | Exercise,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "addAlternativeExercises",
		params: [exercise.title, exercise.id, alternativeExercise.title, alternativeExercise.id],
	});

export const sendAnalyticsEventDeleteAlternativeExercises = (
	exercise: Exercise,
	alternativeExercise: BaseExercise,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "deleteAlternativeExercises",
		params: [exercise.title, exercise.id, alternativeExercise.title, alternativeExercise.id],
	});

const exerciseAnalyticsBodyPartsString = (exercise: Exercise) => exerciseBodyParts(exercise).join(";");

export const sendAnalyticsEventPlayVoiceOver = (exercise: Exercise): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.EXERCISE,
		action: "playVoiceOver",
		params: [exercise.title, exercise.id],
	});

export const sendAnalyticsOpenAlternativeExercisesInfoTooltip = (): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.EXERCISE,
		action: "openAlternativeExercisesInfoTooltip",
	});

export const sendAnalyticsEventExerciseDetailsViewVideoOrKeyframe = (
	exerciseId: string,
	isKeyframeDisplayed: boolean,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.EXERCISE,
		action: isKeyframeDisplayed ? "viewVideo" : "viewKeyframe",
		params: [exerciseId],
	});

export const sendAnalyticsEventExerciseDetailsComment = (
	programId: string,
	contextAnalyticsCategory: string,
	currentExercise: Exercise,
	initComment?: ExerciseComment,
): void => {
	if (initComment !== currentExercise.comment) {
		let action = "editComment";
		if (initComment === undefined) {
			action = "addComment";
		} else if (currentExercise.comment === undefined) {
			action = "removeComment";
		}
		analyticsService.trackEvent({
			category: AnalyticsCategory.EXERCISE,
			action: action,
			params: [
				currentExercise.id,
				currentExercise.title,
				programId !== "" ? programId : "unsaved",
				contextAnalyticsCategory,
			],
		});
	}
};

export const sendAnalyticsEventNavigationClick = (clickCount: number): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.EXERCISE,
		action: "navigationClick",
		params: [clickCount],
	});
